// Shared 1920×1080 design-stage scaler. Every screen that authors layout
// at design pixels (1:1 to a 1920×1080 canvas) routes through this so the
// scale formula + CSS-var write live in one place. Previously HomeScreen
// and TeamSelectScreen each duplicated the math, and the new
// SettingsScreen modal pulls the same trick.
//
// Formula: scale = min(innerWidth / STAGE_W, innerHeight / STAGE_H).
// Uniform (no stretch), letterboxes on the larger axis.

export const STAGE_W = 1920;
export const STAGE_H = 1080;

export interface StageScalerOptions {
  /** Element receiving the scale transform. The layout author is
   *  responsible for `position: absolute; top: 50%; left: 50%;` so the
   *  `translate(-50%, -50%)` lands the stage centered. */
  stageEl: HTMLElement;
  /** Optional element that gets `--stage-scale` written as a CSS variable.
   *  HomeScreen uses this so its viewport-anchored HUD pieces (LVL card,
   *  bottom pills) can scale independently of the main stage. Most callers
   *  pass the same element they used for `stageEl`'s container. */
  varEl?: HTMLElement;
  /** Optional 1:1 design height for a viewport-anchored bar (HomeScreen's
   *  floor band). The scaled bar height (`floorBarDesignH * scale`) is
   *  written as `--floor-h` on `varEl`. Passes through unchanged when
   *  omitted. */
  floorBarDesignH?: number;
  /** Override the default 1920×1080 design size — useful for modals whose
   *  card is sized at a different design space (e.g. 1100×760). When
   *  omitted the standard STAGE_W / STAGE_H are used. */
  designW?: number;
  designH?: number;
  /** Cap the computed scale so a tiny modal doesn't get blown up to 5×
   *  on a huge monitor. Defaults to no cap (Number.POSITIVE_INFINITY). */
  maxScale?: number;
}

export class StageScaler {
  private mounted = false;
  private readonly handler: () => void = () => this.update();

  constructor(private readonly opts: StageScalerOptions) {}

  /** Apply once + register a `resize` listener so the stage tracks any
   *  viewport change. Idempotent. */
  start(): void {
    if (this.mounted) return;
    this.mounted = true;
    this.update();
    addEventListener('resize', this.handler);
  }

  /** Detach the resize listener. Safe to call before start(). */
  stop(): void {
    if (!this.mounted) return;
    this.mounted = false;
    removeEventListener('resize', this.handler);
  }

  /** Recompute + write. Exposed so callers that mount the stage AFTER
   *  start() (rare, but the modal does this) can trigger a re-measure
   *  without bouncing the listener. */
  update(): void {
    const w = this.opts.designW ?? STAGE_W;
    const h = this.opts.designH ?? STAGE_H;
    const cap = this.opts.maxScale ?? Number.POSITIVE_INFINITY;
    const s = Math.min(innerWidth / w, innerHeight / h, cap);
    this.opts.stageEl.style.transform = `translate(-50%, -50%) scale(${s})`;
    if (this.opts.varEl) {
      this.opts.varEl.style.setProperty('--stage-scale', String(s));
      if (this.opts.floorBarDesignH !== undefined) {
        this.opts.varEl.style.setProperty(
          '--floor-h',
          `${this.opts.floorBarDesignH * s}px`
        );
      }
    }
  }
}
